/**
 * ZSelect — keyboard + ARIA behaviour for the design-system `.z-select`
 * dropdown (single-select listbox). The visual/tokenised styling lives in the
 * vendored `zulti.css`; this hook restores everything the native `<select>`
 * used to do for free: open/close, arrow navigation, type-ahead, focus
 * management and outside-click / Escape dismissal.
 *
 * Options carry their own `phx-click` (e.g. dispatching a locale change), so
 * keyboard selection simply activates the highlighted option via `.click()`.
 */
const ZSelect = {
  mounted() {
    this.trigger = this.el.querySelector(".zs-trigger");
    this.menu = this.el.querySelector('[role="listbox"]');
    this.options = Array.from(this.el.querySelectorAll('[role="option"]'));

    const selected = this.options.findIndex(
      (o) => o.getAttribute("aria-selected") === "true",
    );
    this.activeIndex = selected >= 0 ? selected : 0;

    this.onTriggerClick = () => this.toggle();
    this.onKeydown = (event) => this.handleKeydown(event);
    this.onDocPointer = (event) => {
      if (!this.el.contains(event.target)) this.close();
    };

    this.trigger.addEventListener("click", this.onTriggerClick);
    this.el.addEventListener("keydown", this.onKeydown);
    document.addEventListener("pointerdown", this.onDocPointer);

    this.options.forEach((opt, i) => {
      opt.addEventListener("pointerenter", () => this.setActive(i));
      // The option's own phx-click applies the value; just close afterwards.
      opt.addEventListener("click", () => this.close());
    });
  },

  destroyed() {
    document.removeEventListener("pointerdown", this.onDocPointer);
  },

  isOpen() {
    return this.el.classList.contains("open");
  },

  toggle() {
    this.isOpen() ? this.close() : this.open();
  },

  open() {
    this.el.classList.add("open");
    this.trigger.setAttribute("aria-expanded", "true");
    this.setActive(this.activeIndex);
    this.menu.focus();
  },

  close() {
    if (!this.isOpen()) return;
    this.el.classList.remove("open");
    this.trigger.setAttribute("aria-expanded", "false");
    this.trigger.removeAttribute("aria-activedescendant");
    this.options.forEach((o) => o.classList.remove("is-active"));
  },

  setActive(index) {
    if (index < 0 || index >= this.options.length) return;
    this.activeIndex = index;
    this.options.forEach((o, i) => o.classList.toggle("is-active", i === index));
    const opt = this.options[index];
    opt.scrollIntoView({ block: "nearest" });
    this.trigger.setAttribute("aria-activedescendant", opt.id);
  },

  handleKeydown(event) {
    if (!this.isOpen()) {
      if (
        event.target === this.trigger &&
        ["ArrowDown", "ArrowUp", "Enter", " "].includes(event.key)
      ) {
        event.preventDefault();
        this.open();
      }
      return;
    }

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        this.setActive(Math.min(this.activeIndex + 1, this.options.length - 1));
        break;
      case "ArrowUp":
        event.preventDefault();
        this.setActive(Math.max(this.activeIndex - 1, 0));
        break;
      case "Home":
        event.preventDefault();
        this.setActive(0);
        break;
      case "End":
        event.preventDefault();
        this.setActive(this.options.length - 1);
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        this.options[this.activeIndex]?.click();
        break;
      case "Escape":
        event.preventDefault();
        this.close();
        this.trigger.focus();
        break;
      case "Tab":
        this.close();
        break;
      default:
        if (event.key.length === 1) {
          const key = event.key.toLowerCase();
          const idx = this.options.findIndex((o) =>
            o.textContent.trim().toLowerCase().startsWith(key),
          );
          if (idx >= 0) this.setActive(idx);
        }
    }
  },
};

export default ZSelect;
